'use client';
import { ReactNode, RefCallback, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, type AnimationProps, m } from 'framer-motion';

import { withAtomProvider } from '../helpers/atom-provider.js';
import { cva, cx } from '../helpers/cva/index.js';
import { createComponent } from '../helpers/create-component.js';
import { forwardRefWithAs } from '../helpers/forward-ref-with-as.js';
import { useKey } from '../helpers/hooks/use-key.js';
import { Button } from '../Button/index.js';
import { ModalHeader } from './ModalHeader/index.js';
import { useDisableScroll } from '../helpers/hooks/use-disable-scroll.js';
import { MotionContainer } from '../helpers/motion.js';
import { useTrapFocus } from '../helpers/hooks/use-trap-focus.js';
import { useMergeRefs } from '../helpers/hooks/use-merge-refs.js';
import { useLockBodyScroll } from '../helpers/hooks/use-lock-body-scroll.js';
import CrossIcon from '../Icon/icons/CrossIcon/index.js';
import type { OverrideProps, HTMLComponentProps } from '../../types/helpers.js';
import {
  createModalState,
  ModalContentStyle,
  ModalContext,
  useModalStateAtoms,
} from './ModalState/index.js';
import { useBrand } from '../../brand/context/index.js';
import { useSyncAtomValue } from '../helpers/hooks/use-sync-atom-value.js';

export type ModalProps = OverrideProps<
  [
    HTMLComponentProps<'div'>,
    {
      /**
       * Accepts a selector to auto focus.
       */
      autoFocus?: string;
      /**
       * Should the modal render the "compact" treatment?
       * It will be snug to the top of the frame and have
       * a tighter padding.
       * @default false
       */
      compact?: boolean;
      /**
       * Should the content be styled inside of a white card
       * or sit directly on the colored background.
       * @default undefined
       */
      contentStyle?: ModalContentStyle;
      /**
       * Emit a close event.
       */
      onClose?: () => void;
      /**
       * Is the modal open?
       * @default false
       */
      open?: boolean;
      /**
       * A title to display above the content.
       */
      title?: ReactNode;
      /**
       * The color type to use for the modal.
       * @default 'dark'
       */
      type?: 'dark' | 'light';
      /**
       * A max width to set for the header/content/footer.
       */
      width?: number;
    },
  ]
>;

/**
 * Simple wrapper component for disabling scroll, since we can't
 * call this hook conditionally from inside of Modal.
 */
const DisableScroll = () => {
  useDisableScroll(true);
  return null;
};

/**
 * Simple wrapper component for trapping focus, since we can't
 * call this hook conditionally from inside of Modal.
 */
const TrapFocus = ({
  el,
  initialSelector,
}: {
  el: HTMLElement | null;
  initialSelector?: string;
}) => {
  useTrapFocus(el, true, initialSelector);
  return null;
};

const getModalInnerContainerClasses = cva({
  base: 'cur-bottom-0 cur-absolute cur-w-full cur-left-0 cur-flex cur-flex-col cur-items-center',
  variants: {
    brand: {
      curology: '',
      agency: '',
    },
    compact: {
      true: 'cur-h-[calc(100%-env(safe-area-inset-top))]',
      false: 'cur-h-[calc(100%-3rem-env(safe-area-inset-top))]',
    },
    type: {
      dark: 'cur-bg-surface-brand',
      light: 'cur-bg-surface-primary',
    },
  },
  compoundVariants: [
    {
      brand: 'agency',
      class: 'cur-bg-surface-brand-light',
    },
  ],
});

const getModalDialogClasses = cva({
  base: 'cur-relative cur-flex cur-h-full lg:cur-max-w-6xl cur-max-w-[100vw] cur-grow cur-flex-col focus-visible:cur-outline-0 focus-visible:cur-ring-0 focus-visible:cur-ring-transparent focus-visible:cur-ring-offset-0 focus-visible:cur-ring-offset-transparent cur-px-4 lg:cur-px-6',
  variants: {
    brand: {
      curology: '',
      agency: '',
    },
    type: {
      dark: 'cur-text-inverse',
      light: '',
    },
  },
  compoundVariants: [
    {
      brand: 'agency',
      class: 'cur-text-primary',
    },
  ],
});

const getTitleWrapperClasses = cva({
  base: 'cur-flex cur-items-start cur-justify-between cur-gap-2',
  variants: {
    compact: {
      true: 'cur-my-3',
      false: 'cur-mb-6 cur-mt-12 lg:cur-mb-12',
    },
  },
});

/**
 * The animation props for the blurred background.
 * Waits for the content to animate in before entering.
 */
const backgroundAnimation: AnimationProps = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: {
      duration: 0.1,
      delay: 0.25,
    },
  },
  exit: { opacity: 0 },
  transition: { type: 'linear' },
};

/**
 * The animation props for the modal's content.
 * Waits for the background to animate out before exiting.
 */
const contentAnimation: AnimationProps = {
  initial: { opacity: 0, translateY: '100%' },
  animate: {
    opacity: 1,
    translateY: '0%',
  },
  exit: {
    opacity: 0,
    translateY: '100%',
    transition: {
      duration: 0.25,
      delay: 0.1,
    },
  },
  transition: { ease: 'easeInOut' },
};

/**
 * Modal
 */
export const Modal = withAtomProvider(
  createComponent(
    'Modal',
    forwardRefWithAs<'div', ModalProps>(
      (
        {
          autoFocus,
          children,
          className,
          compact = false,
          contentStyle,
          onClose,
          open = false,
          title,
          type = 'dark',
          width,
          ...props
        },
        ref,
      ) => {
        const brand = useBrand();

        // Close when escape is pressed
        useKey('Escape', onClose, { enabled: open });

        // Store a reference to the element itself for using with the focus trap
        const [modalEl, setModalEl] = useState<HTMLElement | null>(null);
        const modalElRef = useCallback<RefCallback<HTMLElement>>(
          (newModalEl) => setModalEl(newModalEl),
          [],
        );
        const mergedRefs = useMergeRefs(ref, modalElRef);

        useLockBodyScroll(open);

        const { compactAtom, contentStyleAtom } = useModalStateAtoms();
        useSyncAtomValue(compactAtom, compact);
        useSyncAtomValue(contentStyleAtom, contentStyle);

        if (typeof window !== 'object') {
          return null;
        }

        return createPortal(
          <MotionContainer>
            <AnimatePresence>
              {open ? (
                <div
                  ref={mergedRefs}
                  {...props}
                  className={cx(
                    'cur-fixed cur-left-0 cur-top-0 cur-z-[250] cur-h-full cur-w-full',
                    className,
                  )}
                >
                  <DisableScroll />
                  <TrapFocus el={modalEl} initialSelector={autoFocus} />
                  <m.div
                    aria-hidden
                    className="cur-absolute cur-left-0 cur-top-0 cur-z-0 cur-h-full cur-w-full cur-backdrop-blur-sm"
                    tabIndex={-1}
                    onClick={onClose}
                    title="Close"
                    {...backgroundAnimation}
                  />
                  <m.div
                    role="dialog"
                    aria-modal
                    className={getModalInnerContainerClasses({
                      brand,
                      compact,
                      type,
                    })}
                    data-testid="modal-inner-container"
                    {...contentAnimation}
                  >
                    <div
                      className={getModalDialogClasses({ brand, type })}
                      style={{
                        width: width || '100%',
                      }}
                      tabIndex={0}
                      data-testid="modal-content"
                    >
                      <div className={getTitleWrapperClasses({ compact })}>
                        {typeof title === 'string' ? (
                          <ModalHeader>{title}</ModalHeader>
                        ) : (
                          title
                        )}
                        <Button
                          icon={<CrossIcon />}
                          title="Close"
                          onClick={onClose}
                          size="sm"
                          buttonType="secondary"
                          className="cur-shrink-0"
                        />
                      </div>
                      {children}
                    </div>
                  </m.div>
                </div>
              ) : null}
            </AnimatePresence>
          </MotionContainer>,
          document.body,
        );
      },
    ),
  ),
  ModalContext,
  createModalState,
);
